import React, { useMemo, useState } from 'react';

/**
 * Visual + JSON mapping editor untuk ChatGPT templates.
 *
 * Visual mode: user fill tag sahaja (cth "@IMAGE", "image_prompt").
 *   System auto-build path → $.set{N}.{tag}
 *
 * Advanced (JSON) mode: terus edit raw JSON config — untuk struktur kompleks
 *   (cth array index $.scenes[0].image_prompt).
 *
 * Props:
 *   value: string — JSON mapping config (boleh empty)
 *   onChange: (newJsonString) => void
 *   sceneCount: 1 | 2
 */

const FIELDS_1_SCENE = [
    { key: 'promptImage', label: 'Image Prompt', placeholder: '@IMAGE' },
    { key: 'promptVideo', label: 'Video Prompt', placeholder: '@VIDEO' },
    { key: 'captionTiktok', label: 'Caption TikTok', placeholder: '@CAPTION' },
];

const FIELDS_2_SCENE = [
    { key: 'promptImage', label: 'Image Prompt', placeholder: '@IMAGE' },
    { key: 'promptVideoScene1', label: 'Video Prompt Scene 1', placeholder: '@VIDEO_SCENE1' },
    { key: 'promptVideoScene2', label: 'Video Prompt Scene 2', placeholder: '@VIDEO_SCENE2' },
    { key: 'captionTiktok', label: 'Caption TikTok', placeholder: '@CAPTION' },
];

// N-scene (cth FLOWNITY): 1 set sahaja, setiap babak ada Video Prompt + Skrip, tambah 1 caption.
// Default tag = nama field output GPT (cth promptVideoScene1) → mapping identity, admin boleh override.
function buildNSceneFields(sceneCount) {
    const n = Math.max(2, Math.min(8, Number(sceneCount) || 5));
    const out = [];
    // key = field SEBENAR yang card baca (promptVideo${i}); placeholder = tag output GPT (promptVideoScene${i}).
    for (let i = 1; i <= n; i++) {
        out.push({ key: `promptVideo${i}`, label: `Scene ${i} — Video Prompt`, placeholder: `promptVideoScene${i}` });
        out.push({ key: `videoScript${i}`, label: `Scene ${i} — Skrip`, placeholder: `videoScript${i}` });
    }
    out.push({ key: 'captionTiktok', label: 'Caption TikTok', placeholder: 'captionTiktok' });
    return out;
}

// Convert path "$.set1.@IMAGE" → tag "@IMAGE" for set1
function pathToTag(path, setKey) {
    if (!path || typeof path !== 'string') return '';
    let p = path.trim();
    if (p.startsWith('$.')) p = p.slice(2);
    else if (p.startsWith('$')) p = p.slice(1);
    const prefix = `${setKey}.`;
    if (p.startsWith(prefix)) return p.slice(prefix.length);
    return p; // path doesn't match expected pattern — return as-is for visibility
}

// Build path "$.set1.@IMAGE" from tag + setKey
function tagToPath(tag, setKey) {
    if (!tag || !tag.trim()) return '';
    return `$.${setKey}.${tag.trim()}`;
}

// Parse JSON mapping → { 'set1.promptImage': '@IMAGE', ... } (visual tags)
function parseMappingToVisual(jsonStr) {
    if (!jsonStr || !jsonStr.trim()) return {};
    try {
        const parsed = JSON.parse(jsonStr);
        if (!parsed || typeof parsed !== 'object') return {};
        const visual = {};
        for (const [target, source] of Object.entries(parsed)) {
            const dotIdx = target.indexOf('.');
            if (dotIdx <= 0) continue;
            const setKey = target.slice(0, dotIdx); // 'set1'
            visual[target] = pathToTag(source, setKey);
        }
        return visual;
    } catch (e) {
        return {};
    }
}

// Build JSON mapping from visual { 'set1.promptImage': '@IMAGE', ... }
function visualToMapping(visual) {
    const result = {};
    for (const [target, tag] of Object.entries(visual)) {
        if (!tag || !tag.trim()) continue;
        const dotIdx = target.indexOf('.');
        if (dotIdx <= 0) continue;
        const setKey = target.slice(0, dotIdx);
        result[target] = tagToPath(tag, setKey);
    }
    return result;
}

// Detect if existing JSON is "advanced" (uses non-standard paths) — auto switch to JSON mode
function isAdvancedMapping(jsonStr) {
    if (!jsonStr || !jsonStr.trim()) return false;
    try {
        const parsed = JSON.parse(jsonStr);
        if (!parsed || typeof parsed !== 'object') return false;
        for (const [target, source] of Object.entries(parsed)) {
            if (typeof source !== 'string') return true;
            const dotIdx = target.indexOf('.');
            if (dotIdx <= 0) continue;
            const setKey = target.slice(0, dotIdx);
            const expected = `$.${setKey}.`;
            const altExpected = `${setKey}.`;
            if (!source.startsWith(expected) && !source.startsWith(altExpected) && !source.startsWith('$.')) {
                return true;
            }
            // Detect array index, deep nesting, etc.
            const tail = pathToTag(source, setKey);
            if (tail.includes('[') || tail.includes('.')) return true;
        }
        return false;
    } catch (e) {
        return true;
    }
}

const ChatGPTMappingEditor = ({ value = '', onChange, sceneCount = 1 }) => {
    const [mode, setMode] = useState(() => (isAdvancedMapping(value) ? 'json' : 'visual'));
    const [activeSet, setActiveSet] = useState('set1');
    const isNScene = Number(sceneCount) >= 3;
    const fields = isNScene
        ? buildNSceneFields(sceneCount)
        : (Number(sceneCount) === 2 ? FIELDS_2_SCENE : FIELDS_1_SCENE);
    const setNums = isNScene ? [1] : [1, 2, 3];

    // Visual state derived from value (always synced)
    const visual = useMemo(() => parseMappingToVisual(value), [value]);

    const updateVisualField = (target, tag) => {
        const next = { ...visual, [target]: tag };
        const newMapping = visualToMapping(next);
        const allEmpty = Object.values(next).every(v => !v || !v.trim());
        onChange(allEmpty ? '' : JSON.stringify(newMapping, null, 2));
    };

    // Count filled fields per set (for badge)
    const filledCount = (setKey) => {
        return fields.reduce((acc, f) => {
            const tag = visual[`${setKey}.${f.key}`];
            return acc + (tag && tag.trim() ? 1 : 0);
        }, 0);
    };

    return (
        <div className="space-y-2">
            {/* Mode toggle */}
            <div className="flex items-center justify-between">
                <p className="text-[9px] text-slate-400">
                    {mode === 'visual'
                        ? 'Isi tag ChatGPT untuk setiap field. Kosong = guna default.'
                        : 'Edit JSON terus — untuk path kompleks (cth $.scenes[0].image_prompt).'}
                </p>
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200">
                    <button
                        type="button"
                        onClick={() => setMode('visual')}
                        className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${mode === 'visual' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Visual
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('json')}
                        className={`px-2 py-0.5 rounded text-[9px] font-bold transition-all ${mode === 'json' ? 'bg-white text-violet-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Advanced (JSON)
                    </button>
                </div>
            </div>

            {mode === 'visual' ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
                    {/* Set tabs */}
                    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
                        {setNums.map(n => {
                            const setKey = `set${n}`;
                            const filled = filledCount(setKey);
                            const isActive = activeSet === setKey;
                            return (
                                <button
                                    key={setKey}
                                    type="button"
                                    onClick={() => setActiveSet(setKey)}
                                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-bold transition-all ${isActive
                                        ? 'bg-violet-500 text-white shadow-sm'
                                        : 'text-slate-500 hover:bg-slate-100'
                                        }`}
                                >
                                    SET {n}
                                    {filled > 0 && (
                                        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${isActive ? 'bg-white/25 text-white' : 'bg-violet-100 text-violet-600'}`}>
                                            {filled}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Active set fields */}
                    <div className="space-y-2">
                        {fields.map(f => {
                            const target = `${activeSet}.${f.key}`;
                            return (
                                <div key={f.key} className="space-y-0.5">
                                    <label className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">{f.label}</label>
                                    <input
                                        type="text"
                                        value={visual[target] || ''}
                                        onChange={(e) => updateVisualField(target, e.target.value)}
                                        placeholder={f.placeholder}
                                        className="w-full bg-white border border-slate-200 rounded text-[11px] px-2.5 py-1.5 font-mono focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 transition-all"
                                    />
                                </div>
                            );
                        })}
                    </div>

                    <p className="text-[9px] text-slate-400 italic">
                        Tag akan resolve sebagai <code className="bg-slate-100 px-1 rounded">$.{activeSet}.{'{tag}'}</code>.
                    </p>
                </div>
            ) : (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={8}
                    placeholder={`{\n  "set1.promptImage": "$.set1.@IMAGE"\n}`}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg text-xs p-2.5 font-mono resize-y"
                />
            )}
        </div>
    );
};

export default ChatGPTMappingEditor;
